import type { PropType } from "vue";
import { watch } from "vue";
import type { ResDataFilterType } from "quick-vue-components/commonMix/loadData/types";
import componentsInit from "~/user-center/src/componentsInit";
import { loadDirectives } from "~/user-center/src/directives";
import AliOssUploadList from "~/user-center/src/components/AliOssUploadList/index.vue";
import ExportImageBtn from "~/user-center/src/components/ExportImageBtn/index.vue";
import GoodsUploadImageManage from "~/user-center/src/components/GoodsUploadImageManage/index.vue";
import AliCos from "~/user-center/src/components/SingleImageUpload/AliCos.vue";
import userCenterI18n from "~/user-center/src/utils/i18n";
import { syncDayjsLocale } from "~/user-center/src/utils/elementLocale";
import store from "~/user-center/src/store";
import { setUserCenterRouterInstance } from "~/user-center/src/router";
import { normalizeLegacyLocation } from "~/user-center/routes";

let installed = false;

const USER_CENTER_LOCALE_KEY = "current_user_center_locale";
const NOW_LOCALE_KEY = "now-locale";
const NUXT_LOCALE_KEY = "i18n_current_locale";

function normalizeNuxtLocale(locale: string) {
  const localeMap: Record<string, string> = {
    "zh-Hans": "zh-CN",
    "zh-Hant": "zh-TW",
    zh: "zh-CN",
    tw: "zh-TW",
    vi: "vi_VN",
    ja: "ja_JP",
  };
  return localeMap[locale] || locale;
}

function getLocaleRefValue(localeRef: any) {
  return String(localeRef?.value || localeRef || "");
}

function setLocaleRefValue(localeRef: any, locale: string) {
  if (localeRef && typeof localeRef === "object" && "value" in localeRef) {
    localeRef.value = locale;
  }
}

function setStoredLocale(locale: string) {
  if (!process.client) {
    return;
  }
  window.localStorage.setItem(NOW_LOCALE_KEY, locale);
  window.localStorage.setItem(USER_CENTER_LOCALE_KEY, locale);
  window.localStorage.setItem(NUXT_LOCALE_KEY, locale);
  document.cookie = `${NUXT_LOCALE_KEY}=${encodeURIComponent(locale)}; path=/; max-age=31536000; SameSite=Lax`;
}

async function syncNuxtLocaleFromStorage(nuxtI18n: any, localeRef: any) {
  if (!process.client) {
    return;
  }

  const storedLocale = normalizeNuxtLocale(
    window.localStorage.getItem(NUXT_LOCALE_KEY)
    || window.localStorage.getItem(USER_CENTER_LOCALE_KEY)
    || window.localStorage.getItem(NOW_LOCALE_KEY)
    || "",
  );
  if (!storedLocale || storedLocale === getLocaleRefValue(localeRef)) {
    return;
  }

  if (typeof nuxtI18n?.setLocale === "function") {
    await nuxtI18n.setLocale(storedLocale);
  }
  else if (typeof nuxtI18n?.global?.locale !== "undefined") {
    setLocaleRefValue(nuxtI18n.global.locale, storedLocale);
  }
  else {
    setLocaleRefValue(localeRef, storedLocale);
  }
}

async function mergeUserCenterMessages(nuxtApp: any) {
  const nuxtI18n = nuxtApp.$i18n || nuxtApp.vueApp.config.globalProperties.$i18n;
  const mergeLocaleMessage = nuxtI18n?.mergeLocaleMessage || nuxtI18n?.global?.mergeLocaleMessage;
  if (!mergeLocaleMessage) {
    return;
  }

  userCenterI18n.global.availableLocales.forEach((locale) => {
    const message = userCenterI18n.global.getLocaleMessage(locale);
    if (nuxtI18n?.global?.mergeLocaleMessage) {
      nuxtI18n.global.mergeLocaleMessage(locale, message);
    }
    else {
      mergeLocaleMessage(locale, message);
    }
  });

  const localeRef = nuxtI18n?.locale || nuxtI18n?.global?.locale;
  if (!localeRef) {
    return;
  }

  await syncNuxtLocaleFromStorage(nuxtI18n, localeRef);

  watch(
    () => localeRef.value || localeRef,
    (locale) => {
      const nextLocale = normalizeNuxtLocale(String(locale || "zh-TW"));
      (userCenterI18n.global.locale as any).value = nextLocale;
      syncDayjsLocale(nextLocale);
      setStoredLocale(nextLocale);
    },
    { immediate: true },
  );
}

function patchLegacyNavigation(router: any) {
  if (router.__userCenterNavigationPatched) {
    return;
  }

  const originalPush = router.push.bind(router);
  const originalReplace = router.replace.bind(router);
  const originalResolve = router.resolve.bind(router);

  router.push = (to: any) => originalPush(normalizeLegacyLocation(to));
  router.replace = (to: any) => originalReplace(normalizeLegacyLocation(to));
  router.resolve = (to: any, currentLocation?: any) => originalResolve(normalizeLegacyLocation(to), currentLocation);
  router.__userCenterNavigationPatched = true;
}

export default defineNuxtPlugin(async (nuxtApp) => {
  if (!installed) {
    const [
      quickVueComponents,
      quickFormModule,
      dataTableModule,
    ] = await Promise.all([
      import("quick-vue-components"),
      import("quick-vue-components/QuickForm/src/index"),
      import("quick-vue-components/DataTable/src/index.vue"),
    ]);
    const {
      BaseList,
      DataList,
      DataLoad,
      DataTable,
      PowerfulBtn,
      QuickForm,
      SelectExtend,
    } = quickVueComponents;
    const { addQuickFormMemberComponents } = quickFormModule;
    const { DataTableProps } = dataTableModule as any;

    componentsInit();
    loadDirectives(nuxtApp.vueApp);
    nuxtApp.vueApp.use(store);

    addQuickFormMemberComponents({
      type: ["alisingleupload"],
      Component: AliCos,
    });
    addQuickFormMemberComponents({
      type: ["aliuploads"],
      Component: AliOssUploadList,
    });

    nuxtApp.vueApp.component("DataLoad", DataLoad);
    nuxtApp.vueApp.component("BaseList", BaseList);
    nuxtApp.vueApp.component("PowerfulBtn", PowerfulBtn);
    nuxtApp.vueApp.component("DataList", DataList);
    nuxtApp.vueApp.component("DataTable", DataTable);
    nuxtApp.vueApp.component("QuickForm", QuickForm);
    nuxtApp.vueApp.component("SelectExtend", SelectExtend);
    nuxtApp.vueApp.component("GoodsUploadImageManage", GoodsUploadImageManage);
    nuxtApp.vueApp.component("ExportImageBtn", ExportImageBtn);

    Object.assign(DataTableProps, {
      resDataFilter: {
        type: Function as PropType<ResDataFilterType>,
        default: (response: any) => ({
          list: response.data.dataList || response.data.resultList || response.data.list,
          total: response.data.totalRecord || response.data.totalResults || response.data.total,
        }),
      },
      beforeFetchDataFilter: {
        type: Function as PropType<ResDataFilterType>,
        default: (payload: any) => {
          const { currentPage, pageSize, ...otherData } = payload;
          Object.keys(otherData).forEach((key) => {
            if (typeof otherData[key] === "string" && !otherData[key]) {
              delete otherData[key];
            }
          });
          return {
            ...otherData,
            pageNo: currentPage - 1,
            pageSize,
          };
        },
      },
    });

    installed = true;
  }

  setUserCenterRouterInstance(nuxtApp.$router as any);
  patchLegacyNavigation(nuxtApp.$router);
  await mergeUserCenterMessages(nuxtApp);
});
